/**
 * fuelCostUtils — helpers for the EFMS Fuel Analysis dashboard.
 *
 * Cost model (GET /v3/getFuelManagement):
 * RM values prefer backend fields (`trip_fuel_cost_rm`, `total_energy_cost_rm`).
 * When fuel cost is absent, derive RM from litres × fuel price (Price Registry / row price).
 * When energy cost is absent, derive RM from kWh × tariff rate (Price Registry / row price).
 */
import moment from 'moment-timezone';
import { TARIFF_TYPE_OPTIONS } from '../../inventory/priceRegistry/evTariffMapper';
import {
  buildActiveFuelPriceMap,
  getFuelTypeLabel,
  isKnownFuelType,
} from '../../inventory/priceRegistry/fuelPriceMapper';

export const FLEET_FILTER = {
  MIXED: 'MIXED',
  ICE: 'ICE',
  EV: 'EV',
};

export const FLEET_FILTER_OPTIONS = [
  { value: FLEET_FILTER.MIXED, label: 'Mixed' },
  { value: FLEET_FILTER.ICE, label: 'ICE' },
  { value: FLEET_FILTER.EV, label: 'EV' },
];

export const FLEET_CLASS = {
  ICE: 'ICE',
  EV: 'EV',
  HYBRID: 'HYBRID',
};

const numericOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

/** Map a device `assetType` (Road / Rail / EV1 / EV2 / Hybrid) to a fleet class. */
export const classifyFleet = (assetType) => {
  const type = String(assetType || '').trim().toLowerCase();
  if (type === 'ev1' || type === 'ev2' || type === 'ev') return FLEET_CLASS.EV;
  if (type === 'hybrid') return FLEET_CLASS.HYBRID;
  return FLEET_CLASS.ICE;
};

/**
 * Infer fleet class from breakdown row profile fields (preferred) or device assetType.
 * ICE: fuel_type only; EV: charging_tariff only; Hybrid: both.
 * When the device is EV1/EV2, prefer EV unless the row clearly has both fuel and tariff (hybrid).
 */
export const inferFleetClass = (fuelType, chargingTariff, fallbackAssetType) => {
  const assetFleetClass = classifyFleet(fallbackAssetType);
  const hasFuelType = Boolean(fuelType);
  const hasTariff = Boolean(chargingTariff);

  if (assetFleetClass === FLEET_CLASS.EV) {
    if (hasFuelType && hasTariff) return FLEET_CLASS.HYBRID;
    return FLEET_CLASS.EV;
  }

  if (hasFuelType && hasTariff) return FLEET_CLASS.HYBRID;
  if (hasTariff) return FLEET_CLASS.EV;
  if (hasFuelType) return FLEET_CLASS.ICE;
  return assetFleetClass;
};

/** Normalize explicit capability metadata returned by GET /v3/getFuelManagement. */
export const normalizeFleetClass = (value) => {
  const fleetClass = String(value || '').trim().toUpperCase();
  return Object.values(FLEET_CLASS).includes(fleetClass) ? fleetClass : null;
};

/** API capability metadata wins over device-list inference. */
export const resolveRowFleetClass = (row, meta) => {
  const explicitClass = normalizeFleetClass(row?.apiFleetClass);
  if (explicitClass) return explicitClass;

  if (row?.apiSupportsFuel === true && row?.apiSupportsEnergy === true) {
    return FLEET_CLASS.HYBRID;
  }
  if (row?.apiSupportsFuel === false && row?.apiSupportsEnergy === true) {
    return FLEET_CLASS.EV;
  }
  if (row?.apiSupportsFuel === true && row?.apiSupportsEnergy === false) {
    return FLEET_CLASS.ICE;
  }

  return inferFleetClass(
    row?.apiFuelType,
    row?.apiChargingTariff,
    row?.apiAssetType ?? meta?.assetType,
  );
};

/** Current EV tariff code → RM/kWh from Price Registry (same source as Price Registry UI). */
const EV_TARIFF_CODE_LOOKUP = (() => {
  const lookup = {};
  TARIFF_TYPE_OPTIONS.forEach(({ value, label }) => {
    lookup[value] = value;
    lookup[value.toLowerCase()] = value;
    lookup[label] = value;
    lookup[label.toLowerCase()] = value;
    lookup[label.toUpperCase()] = value;
    lookup[label.toUpperCase().replace(/\s+/g, '_')] = value;
  });
  return lookup;
})();

/** Map API / device tariff strings to Price Registry codes (DC_PUBLIC, …). */
export const normalizeChargingTariffCode = (chargingTariff) => {
  if (!chargingTariff) return null;
  const raw = String(chargingTariff).trim();
  return EV_TARIFF_CODE_LOOKUP[raw]
    || EV_TARIFF_CODE_LOOKUP[raw.toLowerCase()]
    || EV_TARIFF_CODE_LOOKUP[raw.toUpperCase()]
    || EV_TARIFF_CODE_LOOKUP[raw.toUpperCase().replace(/\s+/g, '_')]
    || null;
};

export const buildTariffRateMap = (tariffRows) => {
  const map = {};
  (tariffRows || []).forEach((row) => {
    const code = normalizeChargingTariffCode(row?.tariff) || row?.tariff;
    const rate = numericOrNull(row?.rate);
    if (code && rate !== null && rate >= 0) {
      map[code] = rate;
    }
  });
  return map;
};

const resolveTariffRatePerKwh = (row, chargingTariff, tariffRateByCode = {}) => {
  const fromRow = numericOrNull(row.apiEnergyPricePerKwh);
  if (fromRow !== null && fromRow >= 0) return fromRow;
  const code = normalizeChargingTariffCode(chargingTariff);
  if (!code) return null;
  return numericOrNull(tariffRateByCode[code]);
};

/** Per-asset distance (km) — API mileage or fuelEfficiency × litres (same as km/L definition). */
const resolveRowMileageKm = (row) => {
  const fromApi = numericOrNull(row.apiMileageKm);
  if (fromApi !== null && fromApi > 0) return fromApi;

  const fuelEfficiency = numericOrNull(row.fuelEfficiency);
  const fuelLitres = numericOrNull(row.totalFuelConsumption);
  if (fuelEfficiency !== null && fuelEfficiency > 0 && fuelLitres !== null && fuelLitres > 0) {
    return fuelEfficiency * fuelLitres;
  }
  return null;
};

const resolveEnergyCostRM = (energyKwh, apiCostRM, tariffRatePerKwh) => {
  const fromApi = numericOrNull(apiCostRM);
  if (fromApi !== null && fromApi >= 0) return fromApi;

  if (
    energyKwh !== null
    && energyKwh >= 0
    && tariffRatePerKwh !== null
    && tariffRatePerKwh >= 0
  ) {
    return energyKwh * tariffRatePerKwh;
  }
  return null;
};

/** km/kWh — API value or distance ÷ kWh (mirrors fuel efficiency = km ÷ L). */
const resolveEnergyEfficiencyKmPerKwh = (apiEfficiency, mileageKm, energyKwh) => {
  const fromApi = numericOrNull(apiEfficiency);
  if (fromApi !== null && fromApi > 0) return fromApi;

  if (mileageKm !== null && mileageKm > 0 && energyKwh !== null && energyKwh > 0) {
    return mileageKm / energyKwh;
  }
  return fromApi;
};

/** Recompute energy RM / efficiency after kWh is finalised (e.g. KPI aggregate fill). */
export const recalculateEnergyRowMetrics = (row, tariffRateByCode = {}, options = {}) => {
  const tariffRatePerKwh = resolveTariffRatePerKwh(row, row.chargingTariff, tariffRateByCode);
  let mileageKm = resolveRowMileageKm(row);
  const mileageFallbackKm = Number(options.mileageFallbackKm);
  if ((mileageKm === null || mileageKm <= 0) && Number.isFinite(mileageFallbackKm) && mileageFallbackKm > 0) {
    mileageKm = mileageFallbackKm;
  }
  const mileageFromFuelRow = Number(options.mileageKmFromFuelRow);
  if ((mileageKm === null || mileageKm <= 0) && Number.isFinite(mileageFromFuelRow) && mileageFromFuelRow > 0) {
    mileageKm = mileageFromFuelRow;
  }
  const energyKwh = numericOrNull(row.energyConsumption);

  return {
    ...row,
    tariffRatePerKwh,
    mileageKm,
    energyCostRM: resolveEnergyCostRM(energyKwh, row.apiEnergyCostRM, tariffRatePerKwh),
    energyEfficiency: resolveEnergyEfficiencyKmPerKwh(
      row.apiEnergyEfficiency,
      mileageKm,
      energyKwh,
    ),
  };
};

/** kWh for a breakdown row — authoritative API field, with EV-only fallback when backend uses usage columns. */
const resolveRowEnergyConsumption = (row, fleetClass) => {
  const candidates = [
    numericOrNull(row.apiEnergyConsumption),
    numericOrNull(row.apiEnergyUsage),
  ];

  // Pure EV only — hybrids may carry liquid-fuel litres in totalFuelConsumption.
  if (fleetClass === FLEET_CLASS.EV) {
    candidates.push(numericOrNull(row.totalFuelConsumption));
  }

  const positive = candidates.find((value) => value !== null && value > 0);
  if (positive !== undefined) return positive;

  const anyFinite = candidates.find((value) => value !== null);
  return anyFinite !== undefined ? anyFinite : null;
};

/** ICE + Hybrid burn liquid fuel, so they carry fuel cost columns. */
export const isFuelClass = (fleetClass) => (
  fleetClass === FLEET_CLASS.ICE || fleetClass === FLEET_CLASS.HYBRID
);

/** EV + Hybrid draw energy, so they carry energy (kWh) cost columns. */
export const isEnergyClass = (fleetClass) => (
  fleetClass === FLEET_CLASS.EV || fleetClass === FLEET_CLASS.HYBRID
);

/** Hide unit-specific dashboard sections when the API says the selection cannot supply them. */
export const resolveSectionVisibility = (fleetFilter, fleetClasses) => {
  const classes = (fleetClasses || []).map(normalizeFleetClass).filter(Boolean);
  const hasCapabilityMetadata = classes.length > 0;
  const supportsFuel = !hasCapabilityMetadata || classes.some(isFuelClass);
  const supportsEnergy = !hasCapabilityMetadata || classes.some(isEnergyClass);
  return {
    showFuelSection: fleetFilter !== FLEET_FILTER.EV && supportsFuel,
    showEnergySection: fleetFilter !== FLEET_FILTER.ICE && supportsEnergy,
  };
};

const EV_TARIFF_LABEL_BY_VALUE = TARIFF_TYPE_OPTIONS.reduce((accumulator, { value, label }) => {
  accumulator[value] = label;
  return accumulator;
}, {});

// Spec fuelType enum -> display label for the badge.
const SPEC_FUEL_TYPE_LABELS = {
  RON95: 'RON 95',
  RON97: 'RON 97',
  RON95_BUDI95: 'RON 95 BUDI95',
  RON95_SKPS: 'RON 95 SKPS',
  DIESEL_PENINSULAR: 'Diesel (Peninsular)',
  DIESEL_SABAH_SARAWAK: 'Diesel (Sabah/Sarawak)',
  DIESEL_SKDS: 'Diesel (SKDS)',
  DIESEL_BUDI: 'Diesel (BUDI)',
  CNG: 'CNG',
};

/** Badge label for a fuel grade (RON 95, Diesel…) or an EV tariff (DC Fast…). */
export const getFuelTypeBadgeLabel = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (EV_TARIFF_LABEL_BY_VALUE[raw]) return EV_TARIFF_LABEL_BY_VALUE[raw];
  const upper = raw.toUpperCase();
  if (SPEC_FUEL_TYPE_LABELS[upper]) return SPEC_FUEL_TYPE_LABELS[upper];
  return getFuelTypeLabel(raw);
};

/** Pick the EV tariff code from a fuel-management breakdown row. */
export const resolveApiChargingTariff = (item = {}) => {
  const candidates = [
    item.charging_tariff,
    item.chargingTariff,
    item.ev_tariff,
    item.evTariff,
    item.tariff,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = normalizeChargingTariffCode(candidates[i]);
    if (normalized) return normalized;
  }
  return null;
};

/** Device preset binding — fallback only until breakdown row carries profile fields. */
export const resolveAssetFuelTypes = (device) => {
  const binding = device?.vehiclePreset?.fuelTypeTariff || device?.fuelTypeTariff || {};
  const fuelType = firstDefined(
    binding.fuelType,
    device?.vehiclePreset?.fuelType,
    device?.fuelType,
  ) || null;
  const chargingTariffRaw = firstDefined(
    binding.chargingTariff,
    binding.tariff,
    device?.chargingTariff,
  ) || null;
  const chargingTariff = normalizeChargingTariffCode(chargingTariffRaw) || chargingTariffRaw;
  return { fuelType, chargingTariff };
};

/** Case-insensitive asset name lookup for device preset metadata. */
export const lookupAssetMeta = (metaByName, assetName) => {
  if (!metaByName || !assetName) return null;
  if (metaByName[assetName]) return metaByName[assetName];
  return metaByName[String(assetName).toLowerCase()] ?? null;
};

const assignAssetMetaKeys = (map, key, meta) => {
  const trimmed = String(key || '').trim();
  if (!trimmed) return;
  Object.assign(map, {
    [trimmed]: meta,
    [trimmed.toLowerCase()]: meta,
  });
};

/** Build a lookup of asset name -> { fleetClass, assetType, fuelType, chargingTariff } from the device list. */
export const buildAssetMetaMap = (assets) => {
  const map = {};
  (assets || []).forEach((asset) => {
    if (!asset?.id) return;
    const meta = {
      assetType: asset.assetType || null,
      fleetClass: classifyFleet(asset.assetType),
      fuelType: asset.fuelType || null,
      chargingTariff: normalizeChargingTariffCode(asset.chargingTariff) || asset.chargingTariff || null,
    };
    assignAssetMetaKeys(map, asset.id, meta);
    assignAssetMetaKeys(map, asset.name, meta);
    assignAssetMetaKeys(map, asset.uuid, meta);
  });
  return map;
};

/**
 * Merge breakdown-row profile fields into the meta map for assets missing from the device list.
 * Keeps fleet classification accurate when the device search list is incomplete.
 */
export const mergeTableRowMetaIntoMap = (metaMap, tableRows) => {
  const map = { ...metaMap };
  (tableRows || []).forEach((row) => {
    if (!row?.asset) return;
    if (lookupAssetMeta(map, row.asset)) return;

    const fuelType = row.apiFuelType || null;
    const chargingTariff = normalizeChargingTariffCode(row.apiChargingTariff) || null;
    if (!fuelType && !chargingTariff) return;

    const meta = {
      assetType: null,
      fleetClass: inferFleetClass(fuelType, chargingTariff, null),
      fuelType,
      chargingTariff,
    };
    assignAssetMetaKeys(map, row.asset, meta);
  });
  return map;
};

const SPEC_TO_REGISTRY_FUEL_TYPE = {
  RON95: 'ron95',
  RON97: 'ron97',
  RON95_BUDI95: 'ron95_budi95',
  RON95_SKPS: 'ron95_skps',
  DIESEL_PENINSULAR: 'diesel',
  DIESEL_SABAH_SARAWAK: 'diesel_eastmsia',
  DIESEL_SKDS: 'diesel_skds',
  DIESEL_BUDI: 'diesel_budi',
};

/** Map API / spec fuel type codes to Price Registry keys (ron95, diesel, …). */
export const normalizeFuelTypeRegistryKey = (fuelType) => {
  if (!fuelType) return null;
  const raw = String(fuelType).trim();
  const upper = raw.toUpperCase();
  if (SPEC_TO_REGISTRY_FUEL_TYPE[upper]) return SPEC_TO_REGISTRY_FUEL_TYPE[upper];
  const lower = raw.toLowerCase();
  if (isKnownFuelType(lower)) return lower;
  return null;
};

/** Current fuel grade → RM/L from Price Registry (`GET /v3/fuelPrices?current=true`). */
export const buildFuelPriceMap = (fuelPriceRows) => buildActiveFuelPriceMap(fuelPriceRows);

/**
 * Rows requested per price-history fetch.
 *
 * The query is not filtered by fuelType, so `limit` truncates a single
 * `-effectiveFrom` ordering shared by every grade — a grade whose interval has
 * been open for years can be pushed past the cutoff by newer changes to other
 * grades, losing exactly the row we need. The backend caps this at 5000
 * (MAX_LIMIT, report-service/modules/fuelPriceRegistry.js) and the whole
 * registry is far smaller, so ask for all of it rather than leave a margin
 * that quietly shrinks as the registry grows.
 */
export const FUEL_PRICE_HISTORY_LIMIT = 5000;

/**
 * Build the `/v3/fuelPrices` query backing Fuel Level Signal cost estimates.
 *
 * Only the upper bound is sent (HRPT-2029). The endpoint filters on
 * `effectiveFrom`, so adding `dateStart` would return just the price *changes*
 * dated inside the window and drop the interval that opened before it: a window
 * containing no price change came back empty and every event fell back to
 * today's price. With `dateEnd` alone the filter is `effectiveFrom <= window
 * end`, so the covering interval is included and
 * `resolveFuelPricePerLitreAtTime` picks the right one per event.
 */
export const buildFuelPriceHistoryQuery = (rangeEndKey) => ({
  dateEnd: rangeEndKey,
  limit: FUEL_PRICE_HISTORY_LIMIT,
});

const toEffectiveBoundaryMs = (value, boundary = 'start') => {
  if (!value) return null;
  const trimmed = String(value).trim();
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  const parsed = isDateOnly
    ? moment(trimmed, 'YYYY-MM-DD', true)
    : moment(value);
  if (!parsed.isValid()) return null;
  return boundary === 'end' ? parsed.endOf('day').valueOf() : parsed.startOf('day').valueOf();
};

/**
 * Resolve RM/L for a fuel grade at a specific event timestamp using Price Registry
 * history rows (`effectiveFrom` / `effectiveTo`). Falls back to current prices.
 */
export const resolveFuelPricePerLitreAtTime = (
  fuelPriceRows,
  fuelType,
  timestamp,
  fuelPriceByCode = {},
) => {
  const registryKey = normalizeFuelTypeRegistryKey(fuelType);
  if (!registryKey) return null;

  const eventMs = moment(timestamp).valueOf();
  if (!Number.isFinite(eventMs)) {
    return numericOrNull(fuelPriceByCode[registryKey]);
  }

  const typeRows = (fuelPriceRows || []).filter((row) => row.fuelType === registryKey);
  let bestPrice = null;
  let bestFromMs = -Infinity;

  typeRows.forEach((row) => {
    const fromMs = toEffectiveBoundaryMs(row.effectiveFrom, 'start');
    if (fromMs == null || !Number.isFinite(fromMs) || fromMs > eventMs) return;

    const toMs = row.effectiveTo
      ? toEffectiveBoundaryMs(row.effectiveTo, 'end')
      : null;
    if (toMs != null && Number.isFinite(toMs) && eventMs > toMs) return;

    if (fromMs >= bestFromMs) {
      bestFromMs = fromMs;
      bestPrice = numericOrNull(row.price);
    }
  });

  if (bestPrice != null) return bestPrice;
  return numericOrNull(fuelPriceByCode[registryKey]);
};

/** Estimate refill/drain RM — prefers API `estimatedCostRM`, else Δ litres × price at event time. */
export const estimateFuelEventCostRM = (
  event,
  fuelType,
  fuelPriceRows,
  fuelPriceByCode = {},
) => {
  const fromApi = numericOrNull(event?.estimatedCostRM);
  if (fromApi !== null) return fromApi;

  const filterKey = event?.eventFilterKey;
  if (filterKey !== 'REFILL' && filterKey !== 'DRAIN') return null;

  const deltaLitres = numericOrNull(event?.deltaLitres);
  if (deltaLitres == null || deltaLitres === 0) return null;

  const pricePerLitre = resolveFuelPricePerLitreAtTime(
    fuelPriceRows,
    fuelType,
    event?.time ?? event?.x,
    fuelPriceByCode,
  );
  if (pricePerLitre == null) return null;

  return Number((deltaLitres * pricePerLitre).toFixed(2));
};

/** Format Est. RM for fuel signal events — signed value without currency prefix. */
export const formatEstimatedEventCostRM = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const prefix = numeric > 0 ? '+' : '';
  return `${prefix}${numeric.toFixed(2)}`;
};

const resolveFuelPricePerLitre = (row, fuelType, fuelPriceByCode = {}) => {
  const fromRow = numericOrNull(row.apiFuelPricePerLitre);
  if (fromRow !== null && fromRow >= 0) return fromRow;
  const registryKey = normalizeFuelTypeRegistryKey(fuelType);
  if (!registryKey) return null;
  return numericOrNull(fuelPriceByCode[registryKey]);
};

const resolveTripFuelCostRM = (row, pricePerLitre) => {
  const fromApi = numericOrNull(row.apiTripFuelCostRM);
  if (fromApi !== null && fromApi >= 0) return fromApi;

  const litres = numericOrNull(row.totalFuelConsumption);
  if (pricePerLitre !== null && pricePerLitre >= 0 && litres !== null && litres >= 0) {
    return litres * pricePerLitre;
  }
  return null;
};

/** Litres consumed while idling; null when there is no idle waste. */
export const resolveIdleWasteLitres = (row) => {
  const idlingRatio = numericOrNull(row.idlingRatio);
  const hasIdlingActivity = idlingRatio === null || idlingRatio > 0;

  const fromIdleWaste = numericOrNull(row.apiIdleWasteFuel);
  if (fromIdleWaste !== null && fromIdleWaste > 0) {
    return hasIdlingActivity ? fromIdleWaste : null;
  }

  const fromIdlingField = numericOrNull(row.fuelIdling);
  if (fromIdlingField !== null && fromIdlingField > 0) {
    return hasIdlingActivity ? fromIdlingField : null;
  }

  const total = numericOrNull(row.totalFuelConsumption);
  const moving = numericOrNull(row.fuelMoving);
  if (total !== null && total > 0 && moving !== null && moving > 0 && moving < total) {
    return total - moving;
  }

  if (total !== null && total > 0 && idlingRatio !== null && idlingRatio > 0) {
    return total * (idlingRatio / 100);
  }

  return null;
};

const resolveIdleWasteCostRM = (row, pricePerLitre) => {
  const idleLitres = resolveIdleWasteLitres(row);
  if (idleLitres === null) return null;

  const fromApi = numericOrNull(row.apiIdleWasteCostRM);
  if (fromApi !== null && fromApi >= 0) return fromApi;

  if (pricePerLitre !== null && pricePerLitre >= 0) {
    return idleLitres * pricePerLitre;
  }
  return null;
};

/**
 * Enrich a breakdown row with fleet class, badges and authoritative backend costs.
 * Profile fields and RM values come from the API row; device meta is fallback only.
 */
export const enrichRowWithCosts = (row, meta, tariffRateByCode = {}, fuelPriceByCode = {}) => {
  const fuelType = row.apiFuelType ?? meta?.fuelType ?? null;
  const chargingTariff = normalizeChargingTariffCode(
    row.apiChargingTariff ?? meta?.chargingTariff ?? null,
  );
  const fleetClass = resolveRowFleetClass({
    ...row,
    apiFuelType: fuelType,
    apiChargingTariff: chargingTariff,
  }, meta);
  const energyConsumption = resolveRowEnergyConsumption(row, fleetClass);
  const tariffRatePerKwh = resolveTariffRatePerKwh(row, chargingTariff, tariffRateByCode);
  const mileageKm = resolveRowMileageKm(row);
  const fuelPricePerLitre = resolveFuelPricePerLitre(row, fuelType, fuelPriceByCode);

  return {
    ...row,
    fleetClass,
    fuelType,
    chargingTariff,
    fuelTypeLabel: getFuelTypeBadgeLabel(fuelType),
    chargingTariffLabel: getFuelTypeBadgeLabel(chargingTariff),
    fuelPricePerLitre,
    tariffRatePerKwh,
    mileageKm,
    tripFuelCostRM: resolveTripFuelCostRM(row, fuelPricePerLitre),
    idleWasteFuel: resolveIdleWasteLitres(row),
    idleWasteCostRM: resolveIdleWasteCostRM(row, fuelPricePerLitre),
    energyConsumption,
    energyCostRM: resolveEnergyCostRM(energyConsumption, row.apiEnergyCostRM, tariffRatePerKwh),
    energyEfficiency: resolveEnergyEfficiencyKmPerKwh(
      row.apiEnergyEfficiency,
      mileageKm,
      energyConsumption,
    ),
    idleWasteSpecComplete: row.apiIdleWasteSpecComplete,
  };
};

/** Sum a numeric field across rows; null when no row had a valid value. */
export const sumField = (rows, field) => {
  let hasValid = false;
  const sum = (rows || []).reduce((total, row) => {
    const raw = row?.[field];
    if (raw !== null && raw !== undefined && raw !== '') {
      const value = Number(raw);
      if (Number.isFinite(value)) {
        hasValid = true;
        return total + value;
      }
    }
    return total;
  }, 0);
  return hasValid ? sum : null;
};

/** True when any row reports incomplete idle-waste motion-state coverage. */
export const hasIncompleteIdleWasteSpec = (rows) => (rows || []).some(
  (row) => row.idleWasteSpecComplete === false,
);

/**
 * Total energy KPI — prefer API aggregate; fall back to row/chart sums when the
 * energy block is missing (common for EV fleets where kWh lives on breakdown rows).
 */
export const resolveTotalEnergyKpi = (apiKpi, energyRows, energyChartPoints = []) => {
  const rowSum = sumField(energyRows, 'energyConsumption');
  const chartSum = (energyChartPoints || []).reduce(
    (total, point) => total + (Number.isFinite(Number(point?.y)) ? Number(point.y) : 0),
    0,
  );

  if (apiKpi?.hasValue && Number(apiKpi.value) > 0) {
    return apiKpi;
  }
  if (rowSum !== null && rowSum > 0) {
    return {
      ...apiKpi,
      value: rowSum,
      hasValue: true,
    };
  }
  if (chartSum > 0) {
    return {
      ...apiKpi,
      value: chartSum,
      hasValue: true,
    };
  }
  return apiKpi;
};

/**
 * When the API aggregate KPI has kWh but breakdown rows are empty/zero, fill rows so
 * the table subtotal matches the KPI card (common when kWh is only on the summary block).
 */
export const enrichEnergyRowsWithAggregate = (energyRows, aggregateKwh) => {
  const rows = energyRows || [];
  const total = Number(aggregateKwh);
  if (!Number.isFinite(total) || total <= 0 || rows.length === 0) return rows;

  const rowSum = sumField(rows, 'energyConsumption');
  if (rowSum !== null && rowSum > 0) return rows;

  if (rows.length === 1) {
    return rows.map((row) => ({
      ...row,
      energyConsumption: total,
    }));
  }

  // Split aggregate KPI evenly when per-row kWh is absent (multi-EV fleets).
  const perRow = total / rows.length;
  return rows.map((row) => ({
    ...row,
    energyConsumption: perRow,
  }));
};

const toFuelSeriesTimestamp = (value) => {
  const timestamp = moment(value).valueOf();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const toFuelSeriesLitres = (value) => {
  const litres = Number(value);
  return Number.isFinite(litres) ? litres : 0;
};

/** Guard against invalid backend fuel-efficiency outliers. */
export const normalizeFuelEfficiencyPoint = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0 || numericValue > 100) return null;
  return numericValue;
};

const medianOf = (values = []) => {
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

/** Place daily line-chart points at midday so the series fills the day slot. */
const toDailyLineChartX = (dayMoment) => dayMoment.clone().add(12, 'hours').valueOf();

const resolveLastDayWithData = (sortedDays, dailyByDayAsset, assets, timezone) => {
  for (let index = sortedDays.length - 1; index >= 0; index -= 1) {
    const [dayKey] = sortedDays[index];
    const dayValues = assets
      .map((asset) => dailyByDayAsset.get(dayKey)?.get(asset))
      .filter((val) => val != null && val > 0);
    if (dayValues.length > 0) {
      return moment(dayKey).tz(timezone).endOf('day').valueOf();
    }
  }
  const lastDayKey = sortedDays[sortedDays.length - 1]?.[0];
  return lastDayKey
    ? moment(lastDayKey).tz(timezone).endOf('day').valueOf()
    : null;
};

/** Group hourly fuel consumption rows by asset when the API includes asset_name per point. */
export const extractPerAssetFuelSeriesFromResponse = (res = {}) => {
  const rows = res.fuel_consumption_mileage_with_time
    || res.fuelConsumptionMileageWithTime
    || [];
  const byAsset = new Map();
  let hasAssetField = false;

  rows.forEach((point) => {
    const asset = point.asset_name || point.assetName || null;
    if (asset) hasAssetField = true;
    const x = toFuelSeriesTimestamp(
      point?.bqTimestampHour?.value ?? point?.bq_timestamp_hour?.value,
    );
    const litres = toFuelSeriesLitres(
      point?.fuel_consumption_per_hour ?? point?.fuelConsumptionPerHour,
    );
    if (!asset || x == null || litres <= 0) return;
    if (!byAsset.has(asset)) byAsset.set(asset, []);
    byAsset.get(asset).push({ x, y: litres });
  });

  if (!hasAssetField || byAsset.size === 0) return null;

  return Array.from(byAsset.entries()).map(([asset, points]) => ({
    asset,
    points: points.sort((a, b) => a.x - b.x),
  }));
};

/** Build per-asset hourly fuel series from one mapped payload per asset. */
export const buildPerAssetFuelSeriesFromPayloads = (payloads = []) => payloads.map((payload) => {
  const asset = payload.tableData?.find((row) => row.asset)?.asset
    || payload.tableData?.[0]?.asset
    || null;
  if (!asset) return null;
  const points = (payload.fuelConsumptionChart || [])
    .map((point) => ({ x: point.x, y: point.fuelConsumption }))
    .filter((point) => point.x != null && point.y > 0);
  if (points.length === 0) return null;
  return { asset, points };
}).filter(Boolean);

/** Group hourly fuel-efficiency rows by asset when the API includes asset_name per point. */
export const extractPerAssetFuelEfficiencySeriesFromResponse = (res = {}) => {
  const rows = res.fuel_efficiency_with_time
    || res.fuelEfficiencyWithTime
    || [];
  const byAsset = new Map();
  let hasAssetField = false;

  rows.forEach((point) => {
    const asset = point.asset_name || point.assetName || null;
    if (asset) hasAssetField = true;
    const x = toFuelSeriesTimestamp(
      point?.bqTimestampHour?.value ?? point?.bq_timestamp_hour?.value,
    );
    const efficiency = normalizeFuelEfficiencyPoint(
      point?.fuel_efficiency_per_hour ?? point?.fuelEfficiencyPerHour,
    );
    if (!asset || x == null || efficiency == null) return;
    if (!byAsset.has(asset)) byAsset.set(asset, []);
    byAsset.get(asset).push({ x, y: efficiency });
  });

  if (!hasAssetField || byAsset.size === 0) return null;

  return Array.from(byAsset.entries()).map(([asset, points]) => ({
    asset,
    points: points.sort((a, b) => a.x - b.x),
  }));
};

/** Build per-asset hourly fuel-efficiency series from one mapped payload per asset. */
export const buildPerAssetFuelEfficiencySeriesFromPayloads = (payloads = []) => payloads.map((payload) => {
  const asset = payload.tableData?.find((row) => row.asset)?.asset
    || payload.tableData?.[0]?.asset
    || null;
  if (!asset) return null;
  const points = (payload.fuelEfficiencyChart || [])
    .map((point) => ({ x: point.x, y: point.y }))
    .filter((point) => point.x != null && point.y != null && point.y > 0);
  if (points.length === 0) return null;
  return { asset, points };
}).filter(Boolean);

/** Palette for stacked daily fuel consumption bars — muted Horizon teal/slate tones. */
export const DAILY_FUEL_ASSET_COLORS = [
  '#00868B',
  '#73A2BA',
  '#365A6D',
  '#26404E',
  '#5B9AA8',
  '#4A7C8C',
  '#6B8E9F',
  '#3D6B7A',
];

/** Roll up hourly fuel points into daily stacked-bar series per asset. */
export const buildDailyFuelConsumptionByAsset = (
  assetSeries = [],
  timezone = 'Asia/Kuala_Lumpur',
  rangeStart = null,
  rangeEnd = null,
) => {
  const dayBuckets = new Map();
  const dayTimestamps = new Map();

  assetSeries.forEach(({ asset, points }) => {
    if (!asset) return;
    (points || []).forEach(({ x, y }) => {
      const litres = Number(y);
      if (!Number.isFinite(litres) || litres <= 0 || x == null) return;
      const dayMoment = moment(x).tz(timezone).startOf('day');
      const dayKey = dayMoment.format('YYYY-MM-DD');
      const dayStart = dayMoment.valueOf();
      if (!dayBuckets.has(dayKey)) {
        dayBuckets.set(dayKey, new Map());
        dayTimestamps.set(dayKey, dayStart);
      }
      const bucket = dayBuckets.get(dayKey);
      bucket.set(asset, (bucket.get(asset) || 0) + litres);
    });
  });

  let sortedDays;
  if (rangeStart != null && rangeEnd != null) {
    const days = [];
    const cursor = moment(rangeStart).tz(timezone).startOf('day');
    const endDay = moment(rangeEnd).tz(timezone).startOf('day');
    while (cursor.isSameOrBefore(endDay, 'day')) {
      const dayKey = cursor.format('YYYY-MM-DD');
      days.push([dayKey, cursor.valueOf()]);
      cursor.add(1, 'day');
    }
    sortedDays = days;
  } else {
    sortedDays = Array.from(dayTimestamps.entries()).sort((a, b) => a[1] - b[1]);
  }

  const assets = [...new Set(assetSeries.map((s) => s.asset).filter(Boolean))].sort();

  const series = assets.map((asset) => ({
    name: asset,
    data: sortedDays.map(([dayKey, dayTs]) => {
      const val = dayBuckets.get(dayKey)?.get(asset) || 0;
      return { x: dayTs, y: val > 0 ? Number(val.toFixed(1)) : 0 };
    }),
  }));

  return { series, dayCount: sortedDays.length, assets };
};

/**
 * Roll up hourly fuel-efficiency points into daily km/L per asset plus a fleet median line.
 * Daily value = average of valid hourly readings for that calendar day.
 */
export const buildDailyFuelEfficiencyByAsset = (
  assetSeries = [],
  timezone = 'Asia/Kuala_Lumpur',
  rangeStart = null,
  rangeEnd = null,
) => {
  const dayBuckets = new Map();

  assetSeries.forEach(({ asset, points }) => {
    if (!asset) return;
    (points || []).forEach(({ x, y }) => {
      const efficiency = normalizeFuelEfficiencyPoint(y);
      if (efficiency == null || x == null) return;
      const dayMoment = moment(x).tz(timezone).startOf('day');
      const dayKey = dayMoment.format('YYYY-MM-DD');
      if (!dayBuckets.has(dayKey)) dayBuckets.set(dayKey, new Map());
      const bucket = dayBuckets.get(dayKey);
      if (!bucket.has(asset)) bucket.set(asset, []);
      bucket.get(asset).push(efficiency);
    });
  });

  let sortedDays;
  if (rangeStart != null && rangeEnd != null) {
    const days = [];
    const cursor = moment(rangeStart).tz(timezone).startOf('day');
    const endDay = moment(rangeEnd).tz(timezone).startOf('day');
    while (cursor.isSameOrBefore(endDay, 'day')) {
      days.push([cursor.format('YYYY-MM-DD'), toDailyLineChartX(cursor)]);
      cursor.add(1, 'day');
    }
    sortedDays = days;
  } else {
    sortedDays = Array.from(dayBuckets.keys())
      .sort()
      .map((dayKey) => [dayKey, toDailyLineChartX(moment(dayKey).tz(timezone).startOf('day'))]);
  }

  const assets = [...new Set(assetSeries.map((s) => s.asset).filter(Boolean))].sort();
  const dailyByDayAsset = new Map();

  dayBuckets.forEach((assetMap, dayKey) => {
    const dayValues = new Map();
    assetMap.forEach((values, asset) => {
      if (values.length === 0) return;
      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
      dayValues.set(asset, Number(avg.toFixed(2)));
    });
    dailyByDayAsset.set(dayKey, dayValues);
  });

  const assetApexSeries = assets.map((asset) => ({
    name: asset,
    data: sortedDays.map(([, dayTs]) => {
      const dayKey = moment(dayTs).tz(timezone).format('YYYY-MM-DD');
      const val = dailyByDayAsset.get(dayKey)?.get(asset);
      return { x: dayTs, y: val != null && val > 0 ? val : null };
    }),
  }));

  const medianData = sortedDays.map(([, dayTs]) => {
    const dayKey = moment(dayTs).tz(timezone).format('YYYY-MM-DD');
    const dayValues = assets
      .map((asset) => dailyByDayAsset.get(dayKey)?.get(asset))
      .filter((val) => val != null && val > 0);
    const med = medianOf(dayValues);
    return { x: dayTs, y: med != null ? Number(med.toFixed(2)) : null };
  });

  const allValues = [
    ...assetApexSeries.flatMap((s) => s.data.map((p) => p.y)),
    ...medianData.map((p) => p.y),
  ].filter((val) => val != null && val > 0);

  const peak = allValues.length > 0 ? Math.max(...allValues) : 5;
  const yMax = Math.ceil(peak * 1.1 * 10) / 10;
  const chartRangeStart = sortedDays.length > 0
    ? moment(sortedDays[0][0]).tz(timezone).startOf('day').valueOf()
    : null;
  const chartRangeEnd = sortedDays.length > 0
    ? resolveLastDayWithData(sortedDays, dailyByDayAsset, assets, timezone)
    : null;

  return {
    assetSeries: assetApexSeries,
    medianSeries: { name: 'Fleet median', data: medianData },
    series: [...assetApexSeries, { name: 'Fleet median', data: medianData }],
    dayCount: sortedDays.length,
    yMax,
    assets,
    chartRangeStart,
    chartRangeEnd,
  };
};

/** Format a subtotal RM cell — shows 0.00 when the sum is zero, em dash when unknown. */
export const formatSubtotalCostRM = (value, formatNumber) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return `RM ${formatNumber(value)}`;
};
